import { triviaEvents, triviaNight } from '../config/siteConfig';
import { parseBandDate } from './bandDateUtils';

const DAY_NAMES = {
  'sun': 'Sunday',
  'mon': 'Monday',
  'tue': 'Tuesday',
  'wed': 'Wednesday',
  'thu': 'Thursday',
  'fri': 'Friday',
  'sat': 'Saturday',
};

// How many days ahead counts as "the week of" the event.
// An entry first appears when the event is within this many days (i.e. up to
// 7 days before), and keeps showing through the event day.
const WEEK_OF_WINDOW_DAYS = 7;

/**
 * Get the full day name out of a date string like "Thu, Jun 4" -> "Thursday".
 */
function getDayName(dateStr) {
  if (!dateStr) return null;
  const abbr = dateStr.split(',')[0].trim().toLowerCase().slice(0, 3);
  return DAY_NAMES[abbr] || null;
}

/**
 * Turn a stored time like "9:00PM" into the casual form used in the banner.
 * Drops a ":00" on-the-hour and the meridiem (trivia is an evening thing):
 *   "9:00PM"  -> "9"
 *   "8:30PM"  -> "8:30"
 */
export function formatTriviaTime(time) {
  if (!time) return '';
  let t = time.trim().replace(/\s*(AM|PM)\s*$/i, '');
  t = t.replace(/:00$/, '');
  return t;
}

/**
 * Parse a clock string like "7:00PM", "7:00 PM", "8:30 pm" or "9PM" into
 * 24h { h, m }. Returns null if unparseable.
 */
function parseClock(str) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([AP])M?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const pm = m[3].toUpperCase() === 'P';
  if (pm && h < 12) h += 12;
  if (!pm && h === 12) h = 0;
  return { h, m: min };
}

// Default length of a trivia night when no end time is known.
const DEFAULT_TRIVIA_MINUTES = 90;

/**
 * Absolute start/end Date objects for a trivia occurrence, using its
 * daysUntil offset from today and its time / endTime strings.
 */
export function getTriviaWindow(trivia) {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (trivia.daysUntil || 0));
  const s = parseClock(trivia.time) || { h: 19, m: 0 };
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), s.h, s.m);
  const e = parseClock(trivia.endTime);
  const end = e
    ? new Date(base.getFullYear(), base.getMonth(), base.getDate(), e.h, e.m)
    : new Date(start.getTime() + DEFAULT_TRIVIA_MINUTES * 60 * 1000);
  return { start, end };
}

/**
 * Number of whole days from today (local, midnight) until the given date.
 */
function daysUntil(date) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

/**
 * The standing weekly trivia night (triviaNight config) as a synthetic
 * occurrence. Unlike dated one-offs — which get the full week-of promo
 * window — the weekly repeater only fires ON the night itself, so the
 * celebratory confetti banner lands on trivia night instead of running
 * every single week from Friday onward.
 */
function buildRecurringOccurrence() {
  // "7:00 PM – 8:30 PM" -> start "7:00PM", end "8:30PM"
  const [rawStart, rawEnd] = (triviaNight.time || '').split(/[–—-]/);
  const clean = (s) => (s || '').trim().replace(/\s+/g, '');
  return {
    date: null,
    time: clean(rawStart),
    endTime: clean(rawEnd) || null,
    eventLink: null,
    daysUntil: 0,
    dayName: triviaNight.day,
    recurring: true,
  };
}

function getRecurringTriviaToday() {
  if (!triviaNight?.enabled || !triviaNight.day) return null;
  const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  if (todayName !== triviaNight.day) return null;
  return buildRecurringOccurrence();
}

/**
 * A preview occurrence for designers: the weekly trivia night as if it
 * were tonight, regardless of weekday. Used by the ?trivia URL flag.
 */
export function getPreviewTrivia() {
  if (!triviaNight?.enabled) return null;
  const occ = buildRecurringOccurrence();
  return { ...occ, headline: buildHeadline(occ) };
}

/**
 * Find the trivia night to celebrate: dated one-offs (from Facebook)
 * within the "week of" window, plus the weekly repeater on its night.
 * Returns the soonest match, or null. On a tie, the dated event wins —
 * it may carry an eventLink the repeater doesn't have.
 *
 * Shape: { date, time, eventLink, daysUntil, dayName, headline }
 */
export function getActiveTrivia() {
  const candidates = triviaEvents
    .map((ev) => {
      const parsed = parseBandDate(ev.date);
      if (!parsed) return null;
      const diff = daysUntil(parsed);
      if (diff < 0 || diff > WEEK_OF_WINDOW_DAYS) return null;
      return { ...ev, daysUntil: diff, dayName: getDayName(ev.date) };
    })
    .filter(Boolean);

  const recurring = getRecurringTriviaToday();
  if (recurring) candidates.push(recurring);

  // Tonight's trivia is over once its window has ended — stop celebrating.
  const now = Date.now();
  const live = candidates.filter(
    (c) => c.daysUntil !== 0 || getTriviaWindow(c).end.getTime() > now
  );

  live.sort(
    (a, b) => a.daysUntil - b.daysUntil || (a.recurring ? 1 : 0) - (b.recurring ? 1 : 0)
  );

  const next = live[0];
  if (!next) return null;

  return { ...next, headline: buildHeadline(next) };
}

/**
 * Build the celebratory headline, e.g.:
 *   "TRIVIA TONIGHT @ 9!"
 *   "TRIVIA TOMORROW @ 9!"
 *   "TRIVIA THIS THURSDAY @ 9!"
 */
function buildHeadline({ daysUntil: diff, dayName, time }) {
  const when =
    diff === 0 ? 'TONIGHT'
    : diff === 1 ? 'TOMORROW'
    : `THIS ${(dayName || '').toUpperCase()}`;

  const at = formatTriviaTime(time);
  return at ? `TRIVIA ${when} @ ${at}!` : `TRIVIA ${when}!`;
}
